import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import {
  Users,
  Eye,
  ChartLineUp,
  Calendar,
  CalendarBlank,
  Clock,
  ArrowsClockwise,
  TrendUp,
  Globe,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 10 * 1000; // auto-refresh every 10s

const numberFmt = (n) => (n || 0).toLocaleString('es-AR');

const StatCard = ({ icon: Icon, label, value, accent, pulse, testid }) => (
  <Card
    className="p-4 sm:p-5 border-border flex items-center gap-4"
    data-testid={testid}
  >
    <div
      className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${accent}`}
    >
      <Icon size={22} weight="duotone" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground mb-1">
        {label}
      </div>
      <div className="text-2xl sm:text-3xl font-bold tabular-nums flex items-center gap-2">
        {numberFmt(value)}
        {pulse && (
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00E676] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#00E676]"></span>
          </span>
        )}
      </div>
    </div>
  </Card>
);

export const AnalyticsPanel = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const timerRef = useRef(null);

  const fetchData = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data: d } = await axios.get(`${API_URL}/api/admin/analytics`, {
        withCredentials: true,
      });
      setData(d);
      setLastUpdated(new Date());
      setError('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo cargar estadísticas');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData(true);
    timerRef.current = setInterval(() => fetchData(true), REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, []);

  if (!data && !error) {
    return (
      <Card className="p-8 text-center border-border">
        <ArrowsClockwise size={32} className="mx-auto mb-2 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Cargando estadísticas...</p>
      </Card>
    );
  }

  const lastStr = lastUpdated
    ? lastUpdated.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '—';

  const hourly = data?.hourly || [];
  const maxHourly = Math.max(1, ...hourly.map((h) => h.count));

  return (
    <div className="space-y-5" data-testid="analytics-panel">
      {error && (
        <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-sm">
          {error}
        </div>
      )}

      {/* Live indicator */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00E676] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#00E676]"></span>
          </span>
          <span className="text-sm font-medium">EN VIVO</span>
          <Badge variant="outline" className="text-[10px]">
            auto-actualiza cada 10s
          </Badge>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock size={14} />
          Actualizado: {lastStr}
          <button
            onClick={() => fetchData(false)}
            disabled={refreshing}
            className="ml-2 text-primary hover:underline disabled:opacity-50"
            data-testid="analytics-refresh-button"
          >
            <ArrowsClockwise
              size={14}
              className={`inline ${refreshing ? 'animate-spin' : ''}`}
            />
          </button>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          icon={Users}
          label="Activos ahora"
          value={data?.active_now}
          accent="bg-[#00E676]/15 text-[#00E676]"
          pulse
          testid="stat-active-now"
        />
        <StatCard
          icon={Eye}
          label="Únicos hoy"
          value={data?.visits_today}
          accent="bg-blue-500/15 text-blue-500"
          testid="stat-visits-today"
        />
        <StatCard
          icon={Calendar}
          label="Únicos semana"
          value={data?.visits_week}
          accent="bg-purple-500/15 text-purple-500"
          testid="stat-visits-week"
        />
        <StatCard
          icon={CalendarBlank}
          label="Únicos mes"
          value={data?.visits_month}
          accent="bg-pink-500/15 text-pink-500"
          testid="stat-visits-month"
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          icon={ChartLineUp}
          label="Dispositivos únicos (histórico)"
          value={data?.total_visits}
          accent="bg-foreground/10 text-foreground"
          testid="stat-visits-total"
        />
        <StatCard
          icon={TrendUp}
          label="Recurrentes (2+ días)"
          value={data?.returning_devices}
          accent="bg-orange-500/15 text-orange-500"
          testid="stat-returning"
        />
        <StatCard
          icon={Eye}
          label="Pageviews hoy"
          value={data?.pageviews_today}
          accent="bg-cyan-500/15 text-cyan-500"
          testid="stat-pageviews-today"
        />
        <StatCard
          icon={Globe}
          label="Pageviews totales"
          value={data?.pageviews_total}
          accent="bg-emerald-500/15 text-emerald-500"
          testid="stat-pageviews-total"
        />
      </div>

      <div className="p-3 rounded-lg bg-muted/40 border border-border text-xs text-muted-foreground">
        <strong>ℹ️ Cómo se cuentan:</strong> "Únicos" cuenta cada <em>dispositivo</em> real
        una sola vez (mismo teléfono / navegador ⇒ 1 visita). Recargar la página o volver
        más tarde <em>no</em> infla el número. "Pageviews" cuenta cada vez que se abre una página.
      </div>

      {/* Hourly chart (last 24h) */}
      {hourly.length > 0 && (
        <Card className="p-5 border-border" data-testid="hourly-chart">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold">Tráfico últimas 24 horas</div>
            <Badge variant="outline">{hourly.length} h activas</Badge>
          </div>
          <div className="flex items-end gap-1 h-32">
            {hourly.map((h) => {
              const hh = parseInt(h.hour.slice(11, 13), 10);
              const pct = (h.count / maxHourly) * 100;
              return (
                <div
                  key={h.hour}
                  className="flex-1 flex flex-col items-center justify-end group relative"
                >
                  <div
                    className="w-full rounded-t-sm bg-gradient-to-t from-primary to-[#00E676] transition-all hover:opacity-80"
                    style={{ height: `${Math.max(4, pct)}%` }}
                    title={`${hh}:00 — ${h.count} visitas`}
                  />
                  <div className="text-[9px] text-muted-foreground mt-1 hidden sm:block">
                    {hh}h
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Top pages */}
        <Card className="p-5 border-border" data-testid="top-pages-card">
          <div className="font-semibold mb-3 flex items-center gap-2">
            <Eye size={16} weight="duotone" />
            Páginas más visitadas (30 días)
          </div>
          {(!data?.top_pages || data.top_pages.length === 0) ? (
            <p className="text-sm text-muted-foreground">Aún no hay datos suficientes.</p>
          ) : (
            <div className="space-y-2">
              {data.top_pages.map((p, idx) => {
                const maxC = Math.max(1, ...data.top_pages.map((x) => x.count));
                const pct = (p.count / maxC) * 100;
                return (
                  <div key={idx}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-mono truncate max-w-[60%]" title={p.path}>
                        {p.path}
                      </span>
                      <span className="text-muted-foreground tabular-nums">
                        {numberFmt(p.count)}
                      </span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Traffic sources */}
        <Card className="p-5 border-border" data-testid="sources-card">
          <div className="font-semibold mb-3 flex items-center gap-2">
            <Globe size={16} weight="duotone" />
            Origen del tráfico (30 días)
          </div>
          {(!data?.sources || data.sources.length === 0) ? (
            <p className="text-sm text-muted-foreground">Aún no hay datos suficientes.</p>
          ) : (
            <div className="space-y-2">
              {data.sources.map((s, idx) => {
                const maxC = Math.max(1, ...data.sources.map((x) => x.count));
                const pct = (s.count / maxC) * 100;
                const colors = {
                  Directo: 'bg-muted-foreground',
                  Google: 'bg-blue-500',
                  Facebook: 'bg-blue-600',
                  Instagram: 'bg-pink-500',
                  TikTok: 'bg-fuchsia-500',
                  WhatsApp: 'bg-[#25D366]',
                  'X / Twitter': 'bg-slate-700',
                  Otro: 'bg-orange-500',
                };
                const cls = colors[s.source] || 'bg-primary';
                return (
                  <div key={idx}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium">{s.source}</span>
                      <span className="text-muted-foreground tabular-nums">
                        {numberFmt(s.count)}
                      </span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full ${cls} rounded-full transition-all`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
